import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid2,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { ErrorModal } from "../components/modals/error-modal";
import { searchUser } from "../configuration/pages/users/UsersPage";
import { ChangePassword } from "../configuration/pages/users/components/ChangePassword/ChangePassword";
import { User } from "../configuration/pages/users/models/User";
import { upsertUser } from "../configuration/pages/users/repositories/UsersRepository";
import { HelmetDexteel } from "../helmet/HelmetDexteel";
import { useToken } from "./useToken";

export const Account = () => {
	const { getUserId, getUserName, getFirstName, getLastName } = useToken();

	const getUserNameFromStorage = () => {
		const sessionData = localStorage.getItem("userMESData");
		if (sessionData) {
			const userData = JSON.parse(sessionData);
			return (
				userData.userName || userData.UserName || userData.username || null
			);
		}
		return null;
	};

	const [error, setError] = useState("");
	const [userName, setUserName] = useState<string>("");
	const [userFirstName, setUserFirstName] = useState<string>("");
	const [userLastName, setUserLastName] = useState<string>("");
	const [userIsDomain, setUserIsDomain] = useState<boolean>(false);
	const [userIsActive, setUserIsActive] = useState<boolean>(false);
	const [userProfiles, setUserProfiles] = useState<string>("");
	const [userPermissions, setUserPermissions] = useState<string>("");
	const [userProcedures, setUserProcedures] = useState<string>("");

	const [editFirstName, setEditFirstName] = useState<string>("");
	const [editLastName, setEditLastName] = useState<string>("");
	const [isSaving, setIsSaving] = useState(false);

	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [showSuccess, setShowSuccess] = useState<boolean>(false);
	const [successMessage, setSuccessMessage] = useState<string>("");

	const userId = getUserId();
	const {
		data: rows,
		isError,
		error: e,
		isSuccess,
		isFetched,
	} = searchUser({ userId });

	const editUser = useMutation({
		mutationFn: upsertUser,
		onSuccess: () => {
			setUserFirstName(editFirstName.trim());
			setUserLastName(editLastName.trim());

			// Update localStorage to refresh navbar
			const sessionData = localStorage.getItem("userMESData");
			if (sessionData) {
				const userData = JSON.parse(sessionData);
				userData.firstName = editFirstName.trim();
				userData.lastName = editLastName.trim();
				localStorage.setItem("userMESData", JSON.stringify(userData));
			}

			setShowSuccess(true);
			setSuccessMessage("Profile updated successfully.");
		},
		onError: (error: Error) => {
			setError(error.message);
		},
	});

	const hasChanges = () => {
		return editFirstName !== userFirstName || editLastName !== userLastName;
	};

	const handleSave = async () => {
		if (!hasChanges() || !editFirstName.trim() || !editLastName.trim()) return;

		setIsSaving(true);
		try {
			const userId = getUserId();
			const user: User = {
				UserId: userId,
				UserName: userName,
				FirstName: editFirstName.trim(),
				LastName: editLastName.trim(),
				AuthTypeId: userIsDomain ? 1 : 0,
				IsActive: userIsActive,
				LastLogin: null,
				DefaultAssetId: null,
				ProfilesId: "",
				Profiles: "",
				Permissions: "",
			};

			await editUser.mutateAsync(user);
		} catch (err: any) {
			setError(err.toString());
		} finally {
			setIsSaving(false);
		}
	};

	const handlePasswordChange = (success: boolean) => {
		setShowPasswordModal(false);
		if (success) {
			setShowSuccess(true);
			setSuccessMessage("Password changed successfully.");
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (
			e.key === "Enter" &&
			hasChanges() &&
			editFirstName.trim() &&
			editLastName.trim() &&
			!isSaving
		) {
			handleSave();
		}
	};

	const stringToList = (s: string) => {
		s = s.replace(/,/g, "\n");
		s = s.replace(/ *\([^)]*\) */g, "");
		return s;
	};

	const closeMessage = () => {
		setTimeout(() => {
			setShowSuccess(false);
			setSuccessMessage("");
		}, 2000);
	};

	useEffect(() => {
		if (showSuccess) {
			closeMessage();
		}
	}, [showSuccess]);

	useEffect(() => {
		if (isSuccess && isFetched) {
			const userData = rows ? rows[0] : null;
			if (!userData) return;
			const firstName = userData.FirstName || "";
			const lastName = userData.LastName || "";

			setUserName(userData.UserName);
			setUserFirstName(firstName);
			setUserLastName(lastName);
			setEditFirstName(firstName);
			setEditLastName(lastName);
			setUserIsDomain(!!userData.AuthTypeId);
			setUserIsActive(userData.IsActive);
			setUserProfiles(stringToList((userData.Profiles as string) || ""));
			//setUserProcedures(stringToList(userData.Procedures as string || ""));
			setUserPermissions(stringToList((userData.Permissions as string) || ""));
		}
	}, [isSuccess, isFetched]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<HelmetDexteel title="Account Configuration" />

			<Box p={3} maxWidth="100%">
				{showSuccess && (
					<Paper
						elevation={2}
						style={{
							padding: 16,
							marginBottom: 16,
							backgroundColor: "#c8e6c9",
							color: "#1b5e20",
							display: "flex",
							alignItems: "center",
						}}
					>
						<Typography>{successMessage}</Typography>
					</Paper>
				)}

				<Box>
					<Typography
						variant="h5"
						component="h1"
						gutterBottom
						style={{ fontWeight: 600, marginBottom: 16 }}
					>
						Account Configuration
					</Typography>

					<Grid2 container spacing={3}>
						<Grid2
							size={{
								xs: 12,
								md: 6,
							}}
						>
							<Paper
								style={{ padding: 24, height: "100%", minHeight: "350px" }}
							>
								<Typography
									variant="h6"
									gutterBottom
									style={{ marginBottom: 24, fontWeight: 600 }}
								>
									Account Information
								</Typography>

								<Box mb={2}>
									<TextField
										label="Username"
										value={userName}
										variant="outlined"
										fullWidth
										style={{
											pointerEvents: "none",
											cursor: "default",
										}}
										InputProps={{
											readOnly: true,
										}}
										tabIndex={-1}
									/>
								</Box>

								<Box
									mt={3}
									display="flex"
									flexDirection="column"
									style={{ gap: 4 }}
								>
									<FormControlLabel
										control={<Checkbox checked={userIsDomain} disabled />}
										label="Domain User"
									/>
									<FormControlLabel
										control={<Checkbox checked={userIsActive} disabled />}
										label="Active Account"
									/>
								</Box>
							</Paper>
						</Grid2>

						<Grid2 size={{ xs: 12, md: 6 }}>
							<Paper
								style={{ padding: 24, height: "100%", minHeight: "350px" }}
							>
								<Typography
									variant="h6"
									gutterBottom
									style={{ marginBottom: 24, fontWeight: 600 }}
								>
									Personal Details
								</Typography>

								<Box mb={2}>
									<TextField
										label="First Name"
										value={editFirstName}
										variant="outlined"
										fullWidth
										onChange={(e) => setEditFirstName(e.target.value)}
										onKeyDown={handleKeyDown}
										error={!editFirstName.trim()}
										helperText={
											!editFirstName.trim() ? "First name is required" : ""
										}
									/>
								</Box>

								<Box mb={2}>
									<TextField
										label="Last Name"
										value={editLastName}
										variant="outlined"
										fullWidth
										onChange={(e) => setEditLastName(e.target.value)}
										onKeyDown={handleKeyDown}
										error={!editLastName.trim()}
										helperText={
											!editLastName.trim() ? "Last name is required" : ""
										}
									/>
								</Box>

								<Divider style={{ margin: "24px 0 20px" }} />

								<Box
									mt={2}
									display="flex"
									flexDirection="column"
									style={{ gap: 8 }}
								>
									<Button
										fullWidth
										startIcon={isSaving && <CircularProgress size="1rem" />}
										disabled={
											!hasChanges() ||
											isSaving ||
											!editFirstName.trim() ||
											!editLastName.trim()
										}
										variant="contained"
										color="primary"
										onClick={handleSave}
										style={{ marginBottom: "8px" }}
									>
										Save Changes
									</Button>

									<Button
										fullWidth
										variant="contained"
										color="primary"
										onClick={() => setShowPasswordModal(true)}
									>
										Change Password
									</Button>
								</Box>
							</Paper>
						</Grid2>
					</Grid2>

					<Paper
						elevation={2}
						style={{ padding: 24, marginBottom: 24, marginTop: 24 }}
					>
						<Typography
							variant="h6"
							gutterBottom
							style={{ marginBottom: 24, fontWeight: 600 }}
						>
							Permissions & Access
						</Typography>

						<Grid2 container spacing={3}>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<Typography variant="body1" gutterBottom color="textSecondary">
									Assigned Profiles
								</Typography>
								<textarea
									style={{
										width: "100%",
										minHeight: "300px",
										padding: 16,
										fontFamily: "inherit",
										fontSize: "0.875rem",
										lineHeight: "1.5",
										border: "2px solid #e0e0e0",
										borderRadius: 8,
										backgroundColor: "#fff",
										resize: "vertical",
										outline: "none",
									}}
									placeholder="No profiles assigned"
									readOnly
									value={userProfiles}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 4 }}>
								<Typography variant="body1" gutterBottom color="textSecondary">
									System Permissions
								</Typography>
								<textarea
									style={{
										width: "100%",
										minHeight: "300px",
										padding: 16,
										fontFamily: "inherit",
										fontSize: "0.875rem",
										lineHeight: "1.5",
										border: "2px solid #e0e0e0",
										borderRadius: 8,
										backgroundColor: "#fff",
										resize: "vertical",
										outline: "none",
									}}
									placeholder="No permissions assigned"
									readOnly
									value={userPermissions}
								/>
							</Grid2>

							<Grid2 size={{ xs: 12, md: 4 }}>
								<Typography variant="body1" gutterBottom color="textSecondary">
									Available Procedures
								</Typography>
								<textarea
									style={{
										width: "100%",
										minHeight: "300px",
										padding: 16,
										fontFamily: "inherit",
										fontSize: "0.875rem",
										lineHeight: "1.5",
										border: "2px solid #e0e0e0",
										borderRadius: 8,
										backgroundColor: "#fff",
										resize: "vertical",
										outline: "none",
									}}
									placeholder="No procedures available"
									readOnly
									value={userProcedures}
								/>
							</Grid2>
						</Grid2>
					</Paper>
				</Box>
			</Box>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>

			<ChangePassword
				show={showPasswordModal}
				userId={getUserId()}
				onHide={handlePasswordChange}
			/>
		</>
	);
};
